import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { motion } from 'framer-motion';
import { FaShoppingCart, FaHeart, FaStar, FaTruck } from 'react-icons/fa';
import { recommendationService } from '../services/recommendationService';
import { productService } from '../services/apiService';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../auth/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { formatPrice } from '../utils/currency';
import { RecommendationSkeleton } from './Skeleton';

const ProductRecommendations = ({
  title = "Recommended for You",
  currentProduct = null,
  type = "general", // "general", "similar", "browsing", "order"
  limit = 8
}) => {
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addToCart } = useCart();
  const { isAuthenticated } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchRecommendations = async () => {
      try {
        setLoading(true);

        let allProducts = [];

        // Fetch products based on type
        if (type === 'similar' && currentProduct) {
          // Fetch products from same category
          const result = await productService.getUserProducts({
            category: currentProduct.category,
            limit: 50
          });
          allProducts = result.products || result.data?.products || [];
        } else {
          // Fetch all products or based on preferences
          const preferences = recommendationService.getUserPreferences();
          if (preferences.categories.length > 0) {
            const result = await productService.getUserProducts({
              category: preferences.categories[0],
              limit: 100
            });
            allProducts = result.products || result.data?.products || [];
          } else {
            const result = await productService.getUserProducts({ limit: 100 });
            allProducts = result.products || result.data?.products || [];
          }
        }

        // Map products to frontend format
        const mappedProducts = allProducts.map(product => ({
          id: product._id || product.id,
          name: product.name,
          brand: product.brand,
          price: product.price || 0,
          mrp: product.mrp || null,
          image: product.images?.[0] || product.imageUrl || 'https://via.placeholder.com/200x150?text=Product',
          category: product.category,
          subCategory: product.subCategory,
          partNumber: product.partNumber,
          rating: product.rating || 0,
          reviews: product.reviews || 0,
          stock: product.stock || 0
        }));

        // Get recommendations
        let recommended = [];
        if (type === 'similar' && currentProduct) {
          recommended = recommendationService.getSimilarProducts(mappedProducts, currentProduct);
        } else {
          recommended = recommendationService.getRecommendations(mappedProducts, currentProduct);
        }

        setRecommendations(recommended.slice(0, limit));
      } catch (error) {
        console.error('Error fetching recommendations:', error);
        setRecommendations([]);
      } finally {
        setLoading(false);
      }
    };

    fetchRecommendations();
  }, [currentProduct, type, limit]);

  const handleAddToCart = async (e, product) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    try {
      const cartProduct = {
        id: product.id,
        name: product.name,
        brand: product.brand,
        price: product.mrp || product.price,
        discountPrice: product.mrp && product.mrp > product.price ? product.price : null,
        imageUrl: product.image,
        partNumber: product.partNumber,
        quantity: 1
      };
      await addToCart(cartProduct, false);
      toast.success(`${cartProduct.name} added to cart!`);
    } catch (error) {
      toast.error('Failed to add to cart');
    }
  };

  if (loading) {
    return <RecommendationSkeleton count={limit > 4 ? limit : 4} title={title} />;
  }

  if (recommendations.length === 0) {
    return null;
  }

  return (
    <div className="py-6 sm:py-8">
      <h2 className={`text-xl sm:text-2xl font-bold mb-4 sm:mb-6 px-4 sm:px-0 ${
        isDark ? 'text-slate-100' : 'text-gray-900'
      }`}>
        {title}
      </h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 gap-3 sm:gap-4 px-4 sm:px-0">
        {recommendations.map((product, index) => (
          <motion.div
            key={product.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className={`group rounded-2xl overflow-hidden border transition-all duration-500 ${
              isDark
                ? 'border-slate-700 bg-slate-900 shadow-lg shadow-black/25 hover:border-slate-600'
                : 'bg-gradient-to-br from-white via-white to-red-50/20 border-red-100/30 shadow-soft hover:shadow-medium'
            }`}
          >
            <Link
              to={`/product/${product.id}`}
              state={{ product }}
              className="block h-full"
            >
              {/* Product Image */}
              <div className={`aspect-square flex items-center justify-center p-4 relative overflow-hidden ${
                isDark ? 'bg-slate-950/80' : 'bg-gradient-to-b from-gray-50/50 to-red-50/30'
              }`}>
                <div className="absolute inset-0 bg-gradient-to-br from-red-500/5 via-transparent to-orange-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                <div className={`absolute -inset-full bg-gradient-to-r from-transparent to-transparent skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-in-out ${
                  isDark ? 'via-slate-300/10' : 'via-white/20'
                }`} />
                <img loading="lazy"
                  src={product.image}
                  alt={product.name}
                  className="max-w-full max-h-full object-contain transform group-hover:scale-110 transition-transform duration-700 ease-out z-10"
                  onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/200x150?text=Product';
                  }}
                />
              </div>

              {/* Product Info */}
              <div className="p-3 sm:p-4">
                <h3 className={`text-xs sm:text-sm font-bold line-clamp-2 mb-2 min-h-[2.5rem] transition-colors duration-300 ${
                  isDark ? 'text-slate-100 group-hover:text-rose-300' : 'text-gray-800 group-hover:text-red-600'
                }`}>
                  {product.brand} {product.name}
                </h3>

                {/* Price */}
                <div className="mb-3">
                  <div className="flex items-baseline gap-2">
                    <span className="text-sm sm:text-lg font-extrabold text-blue-600">
                      {formatPrice(product.price)}
                    </span>
                    {product.mrp && product.mrp > product.price && (
                      <span className={`text-[10px] sm:text-xs line-through ${
                        isDark ? 'text-slate-500' : 'text-gray-400'
                      }`}>
                        {formatPrice(product.mrp)}
                      </span>
                    )}
                  </div>
                  {product.mrp && product.mrp > product.price && (
                    <span className="text-[10px] bg-red-50 text-red-600 px-1.5 py-0.5 rounded-full font-bold uppercase tracking-wider">
                      {Math.round(((product.mrp - product.price) / product.mrp) * 100)}% OFF
                    </span>
                  )}
                </div>

                {/* Rating */}
                {product.rating > 0 && (
                  <div className="flex items-center gap-1 mb-3">
                    <div className={`flex items-center px-1.5 py-0.5 rounded ${
                      isDark ? 'bg-emerald-950/60 text-emerald-300' : 'bg-green-50 text-green-700'
                    }`}>
                      <FaStar className="text-[10px]" />
                      <span className="text-[10px] font-bold ml-1">{product.rating.toFixed(1)}</span>
                    </div>
                    <span className={`text-[10px] ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                      ({product.reviews})
                    </span>
                  </div>
                )}

                {/* Actions */}
                <div className="flex items-center gap-2 mt-4">
                   <button
                    onClick={(e) => handleAddToCart(e, product)}
                    className="flex-1 bg-red-600 text-white text-xs font-bold px-3 py-2 rounded-xl hover:bg-red-700 active:scale-95 transition-all flex items-center justify-center gap-2 shadow-sm hover:shadow-md"
                  >
                    <FaShoppingCart className="text-[10px]" />
                    <span>Add</span>
                  </button>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    className={`p-2 border rounded-xl transition-all ${
                      isDark
                        ? 'border-slate-600 bg-slate-800 text-slate-300 hover:border-rose-500 hover:text-rose-300'
                        : 'border-gray-200 text-gray-400 hover:bg-red-50 hover:border-red-200 hover:text-red-600'
                    }`}
                  >
                    <FaHeart className="text-xs" />
                  </button>
                </div>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default ProductRecommendations;

